import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import { useAuth } from 'contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveLead } from 'apis/leadCaptureApi'; // Import the API function if needed
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';

// Import components with correct paths
import ContactsTable from 'components/contacts/contactMain/contactsTable/ContactsTable';
import ContactDetailsContainer from 'components/contacts/contactDetails/ContactDetailsContainer';
import AddLeadsModal from 'components/contacts/contactMain/contactsMenu/addLeads/AddLeadsModal';
import CreateLeadModal from 'components/contacts/contactMain/contactsMenu/addLeads/CreateLeadModal';
import ImportViaCSV from 'components/contacts/contactMain/contactsMenu/addLeads/ImportViaCSV';
import ContactsMenu from 'components/contacts/contactMain/contactsMenu/ContactsMenu';
import SearchBar from 'components/contacts/contactMain/SearchBar';

const ContactsContainer = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const {
    contacts,
    isLoadingContacts,
    fetchContacts,
    addContact,
    saveLead: contextSaveLead // Get saveLead from context if available
  } = useLeadCaptureContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [isAddLeadsModalOpen, setIsAddLeadsModalOpen] = useState(false);
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [dateRange, setDateRange] = useState([
    new Date(new Date().getFullYear(), 0, 1), // Start of current year
    new Date(new Date().setHours(23, 59, 59, 999)) // End of today
  ]);
  const addButtonRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load contacts when component mounts
  useEffect(() => {
    if (authState?.user?.userId) {
      fetchContacts(authState.user.userId);
    }
  }, [authState?.user?.userId, fetchContacts]);

  // Filter contacts based on search query and date range
  const filteredContacts = Array.isArray(contacts)
    ? contacts.filter(contact => {
      // First filter by search query
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        (contact.leadName && contact.leadName.toLowerCase().includes(query)) ||
        (contact.leadEmail && contact.leadEmail.toLowerCase().includes(query)) ||
        (contact.leadCompany && contact.leadCompany.toLowerCase().includes(query))
      );

      // Temporarily disable date filtering to debug
      return matchesSearch;

      // Then filter by date range if both dates are valid
      // if (!matchesSearch) return false;
      // 
      // if (dateRange[0] && dateRange[1] && contact.createdAt) {
      //   const contactDate = new Date(contact.createdAt);
      //   return contactDate >= dateRange[0] && contactDate <= dateRange[1];
      // }
      // 
      // return matchesSearch;
    })
    : [];

  // Add debugging for contacts
  useEffect(() => {
    console.log('🔍 ContactsContainer: Raw contacts from context:', {
      contactsLength: contacts?.length,
      isArray: Array.isArray(contacts),
      firstContact: contacts?.[0],
      allContactIds: contacts?.map(c => c._id)
    });

    console.log('🔍 ContactsContainer: Filtered contacts:', {
      filteredLength: filteredContacts.length,
      searchQuery,
      dateRange,
      filteredContactIds: filteredContacts.map(c => c._id)
    });

    // Check if any contacts are being filtered out
    if (contacts && contacts.length > 0 && filteredContacts.length !== contacts.length) {
      console.log('🔍 ContactsContainer: Some contacts are being filtered out!');
      const filteredOut = contacts.filter(contact =>
        !filteredContacts.some(fc => fc._id === contact._id)
      );
      console.log('🔍 ContactsContainer: Filtered out contacts:', filteredOut);
    }
  }, [contacts, filteredContacts, searchQuery, dateRange]);

  // Get selected contacts based on selected IDs
  const getSelectedContacts = () => {
    if (!selectedContactIds.length) return filteredContacts; // If none selected, export all
    return filteredContacts.filter(contact => selectedContactIds.includes(contact._id));
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleContactClick = (contact, goToEditMode = false) => {
    setSelectedContact({
      contact: contact,
      startInEditMode: goToEditMode // Pass the edit mode flag
    });
  };

  const handleBackClick = () => {
    setSelectedContact(null);
  };

  const handleAddLeadsClick = (event) => {
    addButtonRef.current = event.currentTarget;
    setIsAddLeadsModalOpen(true);
  };

  const handleAddLeadsModalClose = () => {
    setIsAddLeadsModalOpen(false);
  };

  const handleAddLeadsModalSelect = (option) => {
    setIsAddLeadsModalOpen(false);
    if (option === 'create') {
      setIsCreateLeadModalOpen(true);
    } else if (option === 'import') {
      setShowImportCSV(true);
    }
  };

  const handleCreateLeadModalClose = () => {
    setIsCreateLeadModalOpen(false);
  };

  const handleImportCSVClose = () => {
    setShowImportCSV(false);
  };

  const handleSelectContacts = (selectedIds) => {
    setSelectedContactIds(selectedIds);
  };

  const handleExport = () => {
    // Handle export functionality
    console.log('Export contacts:', getSelectedContacts());
  };

  const handleRefreshContacts = () => {
    if (authState?.user?.userId) {
      fetchContacts(authState.user.userId);
    }
  };

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange([startDate, endDate]);
    console.log('Date range changed:', startDate, endDate);
  };

  // Handle notifications from CSV import
  const handleNotification = (notification) => {
    setSnackbar(notification);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Choose what to render based on the state
  const renderContent = () => {
    if (selectedContact) {
      return (
        <ContactDetailsContainer
          contact={selectedContact.contact}
          onBack={handleBackClick}
          onDeleteComplete={handleBackClick}
          startInEditMode={selectedContact.startInEditMode} // Pass the flag to start in edit mode
        />
      );
    }

    if (showImportCSV) {
      return (
        <ImportViaCSV
          onBack={handleImportCSVClose}
          onNotification={handleNotification}
        />
      );
    }

    return (
      <>
        <ContactsMenu
          totalContacts={filteredContacts.length}
          onRefresh={handleRefreshContacts}
          onAddLeads={handleAddLeadsClick}
          onExport={handleExport}
          contacts={getSelectedContacts()} // Pass selected contacts
          selectedCount={selectedContactIds.length}
          onDateRangeChange={handleDateRangeChange}
        />
        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
        />
        <ContactsTable
          contacts={filteredContacts}
          onContactClick={handleContactClick}
          isLoading={isLoadingContacts}
          onRefresh={handleRefreshContacts}
          onSelectContacts={handleSelectContacts}
          selectedContactIds={selectedContactIds}
        />
      </>
    );
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      {renderContent()}

      {/* Add Leads Modal */}
      <AddLeadsModal
        isOpen={isAddLeadsModalOpen}
        onClose={handleAddLeadsModalClose}
        onSelect={handleAddLeadsModalSelect}
        anchorEl={addButtonRef.current}
      />

      {/* Create Lead Modal */}
      <CreateLeadModal
        open={isCreateLeadModalOpen}
        onClose={handleCreateLeadModalClose}
        onSuccess={handleRefreshContacts}
      />

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default ContactsContainer;